import {defineStore} from 'pinia'
import {createSlug} from "~/utils/strings";
import type {Campus} from "~/utils/campi";
import type { SelectMenuItem } from '@nuxt/ui'

// --- 1. Interfaces (O Contrato com o PHP) ---

// O Objeto contendo os campos customizados da categoria documentos_publicos
export interface DocumentosPublicos {
    portaria_autorizacao_hash?: string;
    ppc_hash?: string;
}

// O Objeto contendo os campos customizados da categoria ementa_e_afins
export interface EmentaEAfins {
    ementa?: string;
    metodologia?: string;
    modulos?: string[];
    objetivo_geral?: string;
    objetivos_especificos?: string[];
    tempo_estudo_semanal?: number;
}

// O Objeto contendo os campos customizados da categoria estatisticas
export interface Estatisticas {
    total_concluintes?: number;
}

// O Objeto contendo os campos customizados da categoria identificacao
export interface Identificacao {
    area_curso?: string[];
    campus_ofertante?: string[];
    carga_horaria?: number;
    codigo_cbo?: string;
    duracao_indicada_semanas?: number;
    eh_gratuito?: boolean;
    publico_alvo?: string;
    requisitos_ingresso?: string[];
    tem_certificado?: boolean;
}

// O Objeto contendo os campos customizados dos cursos
export interface GroupedFields {
    documentos_publicos: DocumentosPublicos;
    ementa_e_afins: EmentaEAfins;
    estatisticas: Estatisticas;
    identificacao: Identificacao;
}

// O Objeto de um Autor do Curso
export interface Author {
    name: string;
    lattes?: string | null;
    avatar: string;
}

// O Objeto de Curso
export interface Course {
    id: number;
    category: number;
    sortorder: number
    fullname: string;
    shortname: string;
    idnumber: string
    summary?: string;
    summaryformat: string
    format: string
    visible: boolean | number;
    startdate: number;
    enddate: number;
    lang: string;
    timecreated: number;
    timemodified: number;
    aberto_para_visitante: boolean;
    auto_inscricao: 'open' | 'by_password' | null;
    authors: Author[];
    grouped_fields: GroupedFields;
    specs: any[];
    imageurl: string | null;
    campi?: string[] | null;
    search_vector: string;
}

export interface Area {
    name: string;
    categoryId?: number | null;
    courses?: Course[] | null;
}

// O Objeto de Categoria (sem cursos embutidos — cursos ficam em courses[])
export interface Category {
    id: number;
    name: string;
    idnumber: string;
    description: string;
    descriptionformat: string;
    parent: number;
    sortorder: number;
    coursecount: number;
    visible: number;
    visibleold: number;
    timemodified: number;
    depth: number;
    path: string;
    theme: string | null;
    children: Category[];
    imageurl: string | null;
}

// Interface para o Payload que vem do PHP
interface CatalogPayload {
    generated_at: number;
    generation_time_s: number;
    size_bytes: number;
    size_kb: number;
    size_search_bytes: number;
    total_concluintes: number,
    total_ativos: number,
    media_avaliacoes: number,
    seo_courses?: Course[] | null;
    catalog_url?: string | null;
    categories?: Category[] | null;
    courses?: Course[] | null;
}

// Estrutura interna do JSON baixado via AJAX (mesmo shape do payload inline)
interface CatalogJson {
    generated_at: number;
    generation_time_s: number;
    size_bytes: number;
    size_kb: number;
    size_search_bytes: number;
    total_concluintes: number;
    total_ativos: number;
    media_avaliacoes: number;
    seo_courses: Course[];
    categories: Category[];
    courses: Course[];
}

export type SortParam = 'fullname' | 'startdate' | 'carga_horaria' | 'total_concluintes';
export type autoInscricaoParam = 'open' | 'by_password' | null;

export interface CourseFilters {
    aberto_para_visitante?: boolean;
    auto_inscricao?: autoInscricaoParam;
    author_name?: string;
    duracao_semanas_min?: number;
    duracao_semanas_max?: number;
    carga_horaria_min?: number;
    carga_horaria_max?: number;
    keyword?: string;
}

// --- 2. A Store ---

export const useCatalogStore = defineStore('catalog', {
    state: () => ({
        lastUpdated: 0 as number,
        generation_time_s: 0 as number,
        total_concluintes: 0 as number,
        total_ativos: 0 as number,
        media_avaliacoes: 0 as number,
        seo_courses:    [] as Course[],
        catalog_url: null as string | null,
        categories: [] as Category[],
        courses:    [] as Course[],
    }),

    getters: {
        /**
         * Calcula o total de cursos dinamicamente
         */
        total_cursos: (state): number => state.courses.length,

        /**
         * Todos os cursos em lista plana (já estão planos no state — getter por conveniência).
         */
        allCourses: (state): Course[] => state.courses,

        /**
         * Busca um categoria pelo ID.
         */
        getCategoryById: (state) => {
            return (categoryId: number | null): Category | undefined =>
                state.categories.find(c => Number(c.id) === Number(categoryId));
        },

        /**
         * Busca um curso pelo ID.
         */
        getCourseById: (state) => {
            return (courseId: number): Course | undefined =>
                state.courses.find(c => Number(c.id) === Number(courseId));
        },

        /**
         * Busca um curso pelo Slug.
         */
        getCourseBySlug: (state) => {
            return (slug: string | undefined): Course | undefined => {
                if (slug) return state.courses.find(c => createSlug(c.fullname) === String(slug));
                return undefined;
            }
        },

        /**
         * Categorias raiz que possuem cursos (coursecount > 0).
         */
        activeCategories: (state): Category[] =>
            state.categories.filter(cat => Number(cat.coursecount) > 0),

        /**
         * Cursos de uma categoria específica pelo nome (busca case-insensitive).
         * Considera apenas o vínculo direto course.category === category.id.
         */
        getCoursesByCategoryName: (state) => {
            return (name: string): Course[] => {
                const targetName = name.trim().toLowerCase();

                const findCategory = (nodes: Category[]): Category | undefined => {
                    for (const node of nodes) {
                        if (node.name.trim().toLowerCase() === targetName) return node;
                        if (node.children?.length) {
                            const found = findCategory(node.children);
                            if (found) return found;
                        }
                    }
                    return undefined;
                };

                const category = findCategory(state.categories);
                if (!category) return [];
                return state.courses.filter(c => Number(c.category) === Number(category.id));
            };
        },

        /**
         * Cursos de uma categoria E de todas as suas subcategorias (recursivo).
         */
        getAllCoursesUnderCategory: (state) => {
            return (categoryName: string): Course[] => {
                const targetName = categoryName.trim().toLowerCase();

                const findCategory = (nodes: Category[]): Category | undefined => {
                    for (const node of nodes) {
                        if (node.name.trim().toLowerCase() === targetName) return node;
                        if (node.children?.length) {
                            const found = findCategory(node.children);
                            if (found) return found;
                        }
                    }
                    return undefined;
                };

                // Coleta IDs da categoria raiz + todas as descendentes
                const collectIds = (cat: Category): number[] => {
                    const ids = [Number(cat.id)];
                    cat.children?.forEach(child => ids.push(...collectIds(child)));
                    return ids;
                };

                const rootCategory = findCategory(state.categories);
                if (!rootCategory) return [];

                const ids = new Set(collectIds(rootCategory));
                return state.courses.filter(c => ids.has(Number(c.category)));
            };
        },

        /**
         * Retorna os cursos filtrados por uma lista de áreas.
         * Se o array de áreas estiver vazio ou não for passado, retorna todos os cursos.
         */
        getCoursesByAreas(state) {
            return (areas?: string[], excludeID?: number): Course[] => {
                if (!areas || areas.length === 0) return state.courses;

                return state.courses.filter(course => {
                    const courseAreas = course.grouped_fields?.identificacao?.area_curso;
                    if (!courseAreas || courseAreas.length === 0) return false;

                    // Exclui o ID fornecido (muito útil para não recomendar o curso que o usuário já está visualizando)
                    if (excludeID !== undefined && Number(course.id) == Number(excludeID)) return false;

                    return courseAreas.some(area => areas.includes(area));
                });
            };
        },

        /**
         * Retorna uma lista de Áreas extraídas dos cursos.
         * Faz o mapeamento da imagem baseando-se no nome da categoria correspondente
         * e agrupa as referências dos cursos pertencentes à área.
         */
        getAreas: (state): Area[] => {
            const areaMap = new Map<string, Area>();

            for (const course of state.courses) {
                const courseCampiSigas: string[] = course.grouped_fields?.['identificacao']?.['campus_ofertante'] || [];
                const courseCampiNames: string[] = []
                let campus: Campus | undefined
                for (const campusSigla of courseCampiSigas) {
                    campus = campiArray.find(c => c.sigla === campusSigla);
                    if (campus) {
                        courseCampiNames.push(campus?.name)
                    }
                }
                course.campi = courseCampiNames


                // Acessa o array de áreas de forma segura via optional chaining
                const courseAreas: string[] = course.grouped_fields?.['identificacao']?.['area_curso'] || [];

                for (const areaName of courseAreas) {
                    if (!areaMap.has(areaName)) {
                        // Busca a categoria com o mesmo nome para herdar a imageurl
                        const matchedCategory = state.categories.find(
                            cat => cat.name.trim().toLowerCase() === areaName.trim().toLowerCase()
                        );

                        areaMap.set(areaName, {
                            name: areaName,
                            categoryId: matchedCategory ? matchedCategory.id : null,
                            courses: []
                        });
                    }

                    // Adiciona o ponteiro do curso ao array da área correspondente
                    areaMap.get(areaName)!.courses!.push(course);
                }
            }

            // Opcional: Retorna os valores do Map convertidos em Array e ordenados alfabeticamente
            return Array.from(areaMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        },

        /**
         * Retorna uma Área específica (se o slug for passado) ou todas as Áreas (se undefined).
         * Pode ordenar a lista interna de cursos em cada área.
         */
        getAreaBySlug: (state) => {
            return (slugArea?: string, sortBy?: SortParam): Area[] | undefined => {
                // Helper para ordenar os cursos. Faz a cópia rasa aqui para não alterar o state original.
                const sortCourses = (courses: Course[]) => {
                    if (!sortBy) return courses;
                    return [...courses].sort((a, b) => {
                        if (sortBy === 'fullname') return a.fullname.localeCompare(b.fullname);
                        if (sortBy === 'startdate') return b.startdate - a.startdate;
                        if (sortBy === 'carga_horaria') return (a.grouped_fields?.identificacao?.carga_horaria || 0) - (b.grouped_fields?.identificacao?.carga_horaria || 0);
                        if (sortBy === 'total_concluintes') return (b.grouped_fields?.estatisticas?.total_concluintes || 0) - (a.grouped_fields?.estatisticas?.total_concluintes || 0);
                        return 0;
                    });
                };

                const mapArea = (cat: Category): Area => ({
                    name: cat.name,
                    categoryId: cat.id,
                    courses: sortCourses(state.courses.filter(
                        c => c.grouped_fields.identificacao.area_curso?.includes(cat.name))
                    )
                });

                // Se slug for undefined, retorna todas as áreas
                if (slugArea === undefined) {
                    return state.categories.map(mapArea);
                }

                // Busca a área específica
                const category = state.categories.find(c => createSlug(c.name) === slugArea);
                if (!category) return undefined;

                return [mapArea(category), ];
            };
        },

        /**
         * Ranqueamento pelo total de concluintes.
         * Utiliza o getter getCoursesByAreas para filtrar as áreas (opcional).
         * Exclui um curso específico do resultado caso excludeID seja informado.
         */
        topFinishedCourses(state) {
            return (limit: number = 10, areas?: string[], excludeID?: number): Course[] => {
                // Utiliza 'this' para acessar o novo getter
                let coursesToRank = this.getCoursesByAreas(areas, excludeID);

                // Ordena os cursos filtrados e aplica o limite
                return [...coursesToRank]
                    .sort((a, b) => (b.grouped_fields?.estatisticas?.total_concluintes || 0) - (a.grouped_fields?.estatisticas?.total_concluintes || 0))
                    .slice(0, limit);
            };
        },

        /**
         * Filtro master altamente performático (apenas 1 laço de iteração principal).
         */
        filterCourses: (state) => {
            return (
                areaSlug?: string,
                sortBy?: SortParam,
                filters?: CourseFilters
            ): Course[] => {
                let baseCourses = state.courses;

                // Helper para normalizar o termo do usuário (remove acentos e transforma em minúsculo)
                const normalizeForSearch = (str: string) => {
                    if (!str) return '';
                    // .normalize("NFD") separa as letras dos acentos.
                    // O .replace() remove todos os acentos e cê-cedilha.
                    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
                };

                // Filtragem principal por Área (isolamento de domínio)
                if (areaSlug) {
                    // Implementação que obriga a existência da Categoria:
                        // const category = state.categories.find(c => createSlug(c.name) === areaSlug);
                        // if (!category) return []; // Se a área não existe, encerra cedo.
                        // baseCourses = baseCourses.filter(c => c.category === category.id);
                    baseCourses = baseCourses.filter(c => {
                        // Pegamos a lista de áreas de forma segura
                        const areas = c.grouped_fields?.identificacao?.area_curso;
                        // Se o curso não tem áreas, ele não atende ao filtro
                        if (!areas || areas.length === 0) return false;
                        // .some() verifica se PELO MENOS UMA área do curso bate com o slug procurado
                        return areas.some(a => createSlug(a) === areaSlug);
                    });
                }

                let result = baseCourses;

                // Aplicação de múltiplos filtros simultâneos para economizar memória
                if (filters) {
                    // Normaliza o termo pesquisado apenas UMA VEZ fora do laço
                    const searchTerm = normalizeForSearch(filters.keyword || '');
                    // Divide a busca em um array de palavras (ignorando espaços extras)
                    // Ex: "História da Comunicação" vira ['historia', 'da', 'comunicacao']
                    const searchTokens = searchTerm.split(/\s+/).filter(word => word.length > 0);

                    const searchAuthor = normalizeForSearch(filters.author_name || '');

                    result = baseCourses.filter(c => {
                        // Filtros exatos (Booleanos e Strings)
                        if (filters.aberto_para_visitante !== undefined && c.aberto_para_visitante !== filters.aberto_para_visitante) return false;
                        if (filters.auto_inscricao !== undefined && c.auto_inscricao !== filters.auto_inscricao) return false;

                        // 🔍 Busca livre, por tokens
                        if (searchTokens.length > 0 && c.search_vector) {
                            // O método_ .every() garante que TODAS as palavras digitadas existem no vetor.
                            // Se o vetor tiver "historia comunicacao", ele vai encontrar "historia" e "comunicacao".
                            // (A palavra "da" provavelmente existirá em outro lugar do vetor do curso, dando match).
                            const hasAllTokens = searchTokens.every(token => c.search_vector.includes(token));

                            if (!hasAllTokens) {
                                return false; // Falhou na busca textual
                            }
                        }

                        // Busca em Arrays (Autores)
                        if (searchAuthor) { // Busca por autores ignora acentos
                            const hasAuthor = c.authors?.some(a => normalizeForSearch(a.name).includes(searchAuthor));
                            if (!hasAuthor) return false;
                        }

                        // Filtros de Range (Min/Max) com fallback seguro via optional chaining
                        if (c.grouped_fields?.identificacao?.duracao_indicada_semanas) {
                            const duracao = c.grouped_fields?.identificacao?.duracao_indicada_semanas;
                            if (filters.duracao_semanas_min !== undefined && duracao < filters.duracao_semanas_min) return false;
                            if (filters.duracao_semanas_max !== undefined && duracao > filters.duracao_semanas_max) return false;
                        }

                        if (c.grouped_fields?.identificacao?.carga_horaria) {
                            const carga = c.grouped_fields?.identificacao?.carga_horaria;
                            if (filters.carga_horaria_min !== undefined && carga < filters.carga_horaria_min) return false;
                            if (filters.carga_horaria_max !== undefined && carga > filters.carga_horaria_max) return false;
                        }

                        return true;
                    });
                }

                // Ordenação aplicada apenas ao conjunto já reduzido
                if (sortBy) {
                    result = [...result].sort((a, b) => {
                        if (sortBy === 'fullname') return a.fullname.localeCompare(b.fullname);
                        if (sortBy === 'startdate') return a.startdate - b.startdate;
                        if (sortBy === 'carga_horaria') return (a.grouped_fields?.identificacao?.carga_horaria || 0) - (b.grouped_fields?.identificacao?.carga_horaria || 0);
                        if (sortBy === 'total_concluintes') return (b.grouped_fields?.estatisticas?.total_concluintes || 0) - (a.grouped_fields?.estatisticas?.total_concluintes || 0);
                        return 0;
                    });
                }

                return result;
            };
        },
        /**
         * Lista de autores únicos formatada para USelectMenu.
         */
        allAuthors: (state): SelectMenuItem[] => {
          const seen = new Set<string>()
          const authors: SelectMenuItem[] = []

          for (const course of state.courses) {
            for (const author of course.authors ?? []) {
              if (!seen.has(author.name)) {
                seen.add(author.name)
                authors.push({
                  label: author.name,
                  avatar: { src: author.avatar },
                })
              }
            }
          }

          // @ts-ignore
            return authors.sort((a, b) => a.label.localeCompare(b.label))
        },
        /**
         * Calcula os valores mínimo e máximo de carga horária e duração em semanas.
         * Executa em um único loop (O(n)) e minimiza o lookup de propriedades.
         */
        courseFiltersLimits: (state) => {
            // Estrutura de fallback caso o catálogo esteja vazio
            const emptyLimits = {
                cargaHoraria: { min: 0, max: 0 },
                duracaoSemanas: { min: 0, max: 0 }
            };

            if (!state.courses || state.courses.length === 0) {
                return emptyLimits;
            }

            let minCarga = Infinity;
            let maxCarga = -Infinity;
            let minSemanas = Infinity;
            let maxSemanas = -Infinity;

            const totalCursos = state.courses.length;
            for (let i = 0; i < totalCursos; i++) {
                // Salva a referência para evitar buscar na árvore do objeto duas vezes
                // @ts-ignore
                const identificacao = state.courses[i].grouped_fields?.identificacao;

                if (!identificacao) continue;

                // 1. Processa a Carga Horária
                const carga = identificacao.carga_horaria;
                if (carga !== undefined && carga !== null) {
                    if (carga < minCarga) minCarga = carga;
                    if (carga > maxCarga) maxCarga = carga;
                }

                // 2. Processa a Duração em Semanas
                const semanas = identificacao.duracao_indicada_semanas;
                if (semanas !== undefined && semanas !== null) {
                    if (semanas < minSemanas) minSemanas = semanas;
                    if (semanas > maxSemanas) maxSemanas = semanas;
                }
            }

            // Retorna o objeto combinado, tratando os fallbacks caso algum campo venha vazio em todo_ o catálogo
            return {
                cargaHoraria: {
                    min: minCarga === Infinity ? 0 : minCarga,
                    max: maxCarga === -Infinity ? 0 : maxCarga
                },
                duracaoSemanas: {
                    min: minSemanas === Infinity ? 0 : minSemanas,
                    max: maxSemanas === -Infinity ? 0 : maxSemanas
                }
            };
        }

    },

    actions: {
        /**
         * Recebe o payload do Moodle e só atualiza se for mais recente.
         */
        async setCatalog(payload: CatalogPayload) {
            if (!payload) {
                console.warn('[CatalogStore] Payload não enviado para setCatalog');
                return;
            }
            if (!payload.generated_at) {
                console.warn('[CatalogStore] Payload sem generated_at');
                return;
            }

            // TTL: ignora se os dados já são mais recentes
            if (this.courses.length > 0 && payload.generated_at <= this.lastUpdated) {
                console.log(`[CatalogStore] Cache válido (${this.lastUpdated}). Ignorando payload (${payload.generated_at}).`);
                return;
            }

            // Dados inline presentes
            this.lastUpdated         = payload.generated_at;
            this.generation_time_s   = payload.generation_time_s;
            this.total_concluintes   = payload.total_concluintes;
            this.total_ativos        = payload.total_ativos;
            this.media_avaliacoes    = payload.media_avaliacoes;
            this.seo_courses         = payload.seo_courses    ?? [];
            this.catalog_url         = payload.catalog_url    ?? null;
            this.categories          = payload.categories     ?? [];

            // Sem dados inline → busca via AJAX
            if (!payload.courses && payload.catalog_url) {
                console.warn(`[CatalogStore] Sem dados inline. Baixando de: ${payload.catalog_url}`);
                try {
                    const response = await fetch(payload.catalog_url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);

                    const data: CatalogJson = await response.json();
                    this.lastUpdated         = data.generated_at;
                    this.generation_time_s   = data.generation_time_s;
                    this.total_concluintes   = data.total_concluintes;
                    this.total_ativos        = data.total_ativos;
                    this.media_avaliacoes    = data.media_avaliacoes;
                    this.seo_courses         = data.seo_courses;
                    this.catalog_url         = payload.catalog_url;
                    this.categories          = data.categories ?? [];
                    this.courses             = data.courses    ?? [];
                    console.log('[CatalogStore] ✅ Catálogo baixado via AJAX.');
                } catch (error) {
                    console.error('[CatalogStore] Erro ao baixar catálogo:', error);
                }
            } else {
                this.courses = payload.courses ?? [];
            }
            console.log(`[CatalogStore] ✅ Catálogo atualizado (${this.courses.length} cursos, ${this.categories.length} categorias).`);
            return;
        },

        clearCatalog() {
            this.lastUpdated = 0;
            this.generation_time_s = 0;
            this.total_concluintes = 0;
            this.total_ativos = 0;
            this.media_avaliacoes = 0;
            this.seo_courses     = [];
            this.catalog_url = null;
            this.categories  = [];
            this.courses     = [];
        },
    },

    persist: true,
})